import dayjs, { Dayjs } from "dayjs";
import {
  AddressInput,
  CheckBoxInput,
  DateInput,
  DateTimeInput,
  DropDownChoice,
  DropDownOptionSetInput,
  FormFields,
  FormSection,
  MultilineInput,
  OptionSetInput,
  TextInput,
} from "../../../types";
import {
  demandDisplayNames,
  standardEquipmentDisplayNames,
} from "../displayNames";
import { incidentOptionSetsChoices } from "../optionSetChoices";

export type EditDemandStandardEquipment = {
  vdl_adressecompleterecuperation: AddressInput;
  vdl_adressecompletelivraison: AddressInput;
  vdl_identiquealadressedelivraison: CheckBoxInput;
  vdl_datedelivraison: DateInput;
  vdl_horaire: OptionSetInput;
  vdl_datederecuperation: DateInput;
  vdl_horairerecuperation: OptionSetInput;
  vdl_secteurdeladressederecuperation: OptionSetInput;
  vdl_secteurdeladressedelivraison: OptionSetInput;
  vdl_nomdubatimentrecuperation: TextInput;
  vdl_nomdubatimentlivraison: TextInput;
  vdl_remarqueoucommentaire: MultilineInput;
  vdl_remarqueoucommentairerecuperation: MultilineInput;
};

const vdl_horaireChoices: DropDownChoice[] = [{ value: "AM" }, { value: "PM" }];

const editDemandStandardEquipmentFields: EditDemandStandardEquipment = {
  vdl_adressecompleterecuperation: {
    type: "address",
    error: "",
    value: null,
    required: false,
    fillLine: true,
    disabledCondition: (fields) => isAddresseIdentique(fields as EditDemandStandardEquipment),
    requiredCondition: {
      validator: (fields: FormFields) =>
        !fields.vdl_identiquealadressedelivraison.value,
      error: "Veuillez entrer une adress ou sélectionner l'adresse identique",
    },
    label: standardEquipmentDisplayNames.vdl_adressecompleterecuperation,
  },
  vdl_adressecompletelivraison: {
    type: "address",
    error: "",
    value: null,
    required: true,
    fillLine: true,
    label: standardEquipmentDisplayNames.vdl_adressecompletelivraison,
  },
  vdl_datederecuperation: {
    type: "date",
    error: "",
    value: null,
    required: true,
    fillLine: false,
    disablePast: true,
    label: standardEquipmentDisplayNames.vdl_datederecuperation,
    maxDate: minDate6Months,
    minDate: (fields: FormFields) =>
      fields.vdl_datedelivraison.value
        ? (fields.vdl_datedelivraison.value as Dayjs)
        : undefined,
  },
  vdl_horairerecuperation: {
    type: "options",
    error: "",
    value: null,
    required: true,
    fillLine: false,
    choices: incidentOptionSetsChoices.vdl_horaire,
    label: standardEquipmentDisplayNames.vdl_horairerecuperation,
  },
  vdl_datedelivraison: {
    type: "date",
    error: "",
    value: null,
    required: true,
    fillLine: false,
    disablePast: true,
    label: standardEquipmentDisplayNames.vdl_datedelivraison,
    maxDate: minDate6Months,
    minDate: (fields: FormFields) => dayjs().add(6, "days")
  },
  vdl_horaire: {
    type: "options",
    error: "",
    value: null,
    required: true,
    fillLine: false,
    choices: incidentOptionSetsChoices.vdl_horaire,
    label: standardEquipmentDisplayNames.vdl_horaire,
  },
  vdl_secteurdeladressederecuperation: {
    type: "options",
    error: "",
    value: null,
    required: false,
    fillLine: true,
    choices: incidentOptionSetsChoices.vdl_secteurdeladressedelivraison,
    disabledCondition: (fields) => isAddresseIdentique(fields as EditDemandStandardEquipment),
    requiredCondition: {
      validator: (fields: FormFields) =>
        !fields.vdl_identiquealadressedelivraison.value,
      error: "Veuillez entrer un secteur ou sélectionner l'adresse identique",
    },
    label: standardEquipmentDisplayNames.vdl_secteurdeladressederecuperation,
  },
  vdl_secteurdeladressedelivraison: {
    type: "options",
    error: "",
    value: null,
    required: true,
    fillLine: true,
    choices: incidentOptionSetsChoices.vdl_secteurdeladressedelivraison,
    label: standardEquipmentDisplayNames.vdl_secteurdeladressedelivraison,
  },

  vdl_nomdubatimentrecuperation: {
    type: "text",
    error: "",
    value: "",
    required: false,
    fillLine: true,
    label: standardEquipmentDisplayNames.vdl_nomdubatimentrecuperation,
    disabledCondition: (fields) => isAddresseIdentique(fields as EditDemandStandardEquipment),
    requiredCondition: {
      validator: (fields: FormFields) =>
        !fields.vdl_identiquealadressedelivraison.value,
      error:
        "Veuillez entrer un nom de bâtiment ou sélectionner l'adresse identique",
    },
  },
  vdl_nomdubatimentlivraison: {
    type: "text",
    error: "",
    value: "",
    required: false,
    fillLine: true,
    label: standardEquipmentDisplayNames.vdl_nomdubatimentlivraison,
  },
  vdl_remarqueoucommentaire: {
    type: "multiline",
    error: "",
    value: "",
    rows: {
      min: 5,
      max: 5,
    },
    maxLength: 3000,
    fillLine: true,
    label: standardEquipmentDisplayNames.vdl_remarqueoucommentaire,
  },
  vdl_remarqueoucommentairerecuperation: {
    type: "multiline",
    error: "",
    value: "",
    rows: {
      min: 4,
      max: 4,
    },
    maxLength: 3000,
    fillLine: true,
    label: standardEquipmentDisplayNames.vdl_remarqueoucommentairerecuperation,
  },
  vdl_identiquealadressedelivraison: {
    type: "checkbox",
    error: "",
    value: false,
    fillLine: true,
    onChangeEvent: onAdresseIdentiqueChange,
    label: standardEquipmentDisplayNames.vdl_identiquealadressedelivraison,
  },
};

function minDate6Months(fields: FormFields): Dayjs | undefined {
  return dayjs.tz().add(6, "month");
}

function isAddresseIdentique (fields: EditDemandStandardEquipment) : boolean {
  return fields.vdl_identiquealadressedelivraison.value;
}

function onAdresseIdentiqueChange(fields: FormFields): FormFields | undefined {
  try {
    const _fields2 = <EditDemandStandardEquipment>fields;
    let _fields: Partial<EditDemandStandardEquipment> = {};
    _fields.vdl_adressecompleterecuperation = {
      ..._fields2.vdl_adressecompleterecuperation,
      value: null,
      error: "",
    };
    _fields.vdl_secteurdeladressederecuperation = {
      ..._fields2.vdl_secteurdeladressederecuperation,
      value: null,
      error: "",
    };
    _fields.vdl_nomdubatimentrecuperation = {
      ..._fields2.vdl_nomdubatimentrecuperation,
      value: "",
      error: "",
    };
    return _fields;
  } catch (e) {
    return undefined;
  }
}

const editDemandStandardEquipmentLivraisonSection: FormSection<EditDemandStandardEquipment> =
  {
    fields: [
      "vdl_adressecompletelivraison",
      "vdl_secteurdeladressedelivraison",
      "vdl_nomdubatimentlivraison",
      "vdl_datedelivraison",
      "vdl_horaire",
      "vdl_remarqueoucommentaire",
    ],
    title: "Lieu de livraison",
  };

const editDemandStandardEquipmentRecuperationSection: FormSection<EditDemandStandardEquipment> =
  {
    fields: [
      "vdl_identiquealadressedelivraison",
      "vdl_adressecompleterecuperation",
      "vdl_secteurdeladressederecuperation",
      "vdl_nomdubatimentrecuperation",
      "vdl_datederecuperation",
      "vdl_horairerecuperation",
      "vdl_remarqueoucommentairerecuperation",
    ],
    title: "Lieu de récupération",
  };

export {
  editDemandStandardEquipmentFields,
  editDemandStandardEquipmentLivraisonSection,
  editDemandStandardEquipmentRecuperationSection,
};
